import { useState } from "react";
import { useLang } from "../i18n";
import { Icon } from "./Icon";

const S = {
  desc: {
    es: "Recibe cada domingo por la mañana un correo con el pronóstico NWS de la semana y los avisos activos para Puerto Rico. Gratis y sin contraseñas.",
    en: "Receive an email every Sunday morning with the week's NWS forecast and active alerts for Puerto Rico. Free, no passwords.",
  },
  email: { es: "Tu correo electrónico", en: "Your email" },
  subscribe: { es: "Suscribirme", en: "Subscribe" },
  unsubscribe: { es: "Cancelar suscripción", en: "Unsubscribe" },
  sending: { es: "Enviando…", en: "Sending…" },
  successSub: { es: "¡Listo! Te llegará un correo de confirmación.", en: "Done! A confirmation email is on its way." },
  successUnsub: { es: "Suscripción cancelada. ¡Gracias por acompañarnos!", en: "Subscription cancelled. Thanks for being with us!" },
  error: { es: "No pudimos procesar la solicitud. Inténtalo de nuevo.", en: "We could not process the request. Please try again." },
  toUnsub: { es: "¿Ya estás suscrito y quieres cancelar?", en: "Already subscribed and want to cancel?" },
  toSub: { es: "¿Quieres suscribirte?", en: "Want to subscribe?" },
};

const t = (o, lang) => o[lang] ?? o.es;

const inputCls =
  "w-full rounded-md border-2 border-slate-300 bg-white px-4 py-2.5 text-base text-slate-900 placeholder:text-slate-400 focus:border-amber-600 focus:outline-none";

export function SubscribeForm() {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState("sub");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/${mode === "sub" ? "subscribe" : "unsubscribe"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, lang }),
      });
      if (!r.ok) throw new Error("failed");
      setStatus(mode === "sub" ? "ok-sub" : "ok-unsub");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  if (status === "ok-sub" || status === "ok-unsub") {
    return (
      <div data-testid="subscribe-success" className="rounded-md border border-green-700 bg-green-50 p-5 flex items-center gap-3">
        <Icon name="CheckCircle2" className="w-6 h-6 shrink-0 text-green-700" />
        <p className="text-base font-semibold text-green-900">{t(status === "ok-sub" ? S.successSub : S.successUnsub, lang)}</p>
      </div>
    );
  }

  return (
    <form data-testid="subscribe-form" onSubmit={submit} className="grid gap-4">
      <p className="text-sm text-slate-600">{t(S.desc, lang)}</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <label className="block flex-1">
          <span className="mb-1.5 block text-sm font-bold text-slate-600">{t(S.email, lang)} *</span>
          <input
            data-testid="subscribe-email-input"
            type="email"
            required
            maxLength={120}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@correo.com"
            className={inputCls}
          />
        </label>
        <button
          data-testid="subscribe-submit"
          type="submit"
          disabled={status === "sending"}
          className="self-end inline-flex items-center justify-center gap-2 rounded-md bg-[#1C2B4A] px-6 py-2.5 text-base font-bold text-white hover:bg-amber-700 transition-colors duration-200 active:scale-[0.98] disabled:opacity-60"
        >
          <Icon name="Bell" className="w-4 h-4" />
          {status === "sending" ? t(S.sending, lang) : t(mode === "sub" ? S.subscribe : S.unsubscribe, lang)}
        </button>
      </div>
      {status === "error" && (
        <p data-testid="subscribe-error" className="text-sm font-semibold text-red-700">{t(S.error, lang)}</p>
      )}
      <button
        data-testid="subscribe-mode-toggle"
        type="button"
        onClick={() => { setMode((m) => (m === "sub" ? "unsub" : "sub")); setStatus("idle"); }}
        className="w-fit text-xs font-semibold text-slate-400 underline hover:text-amber-700 transition-colors duration-150"
      >
        {t(mode === "sub" ? S.toUnsub : S.toSub, lang)}
      </button>
    </form>
  );
}
